import { useState, useEffect, useRef, useCallback } from 'react';
import { COMMANDS_FLAT } from '../constants/speechConstants';
import { insertRawTextIntoQuill } from '../utils/editorUtils';

const useSpeechRecognition = (quillRef, dictationLang, showError, handleClear) => {
    const [listening, setListening] = useState(false);
    const [browserSupportsSpeechRecognition, setBrowserSupportsSpeechRecognition] = useState(false);
    const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState(true);
    const [interimText, setInterimText] = useState(''); // State for interim results display
    const isListeningRef = useRef(false); // Tracks if the recognition service is actively listening
    const listeningIntentRef = useRef(listening); // Tracks the user's *intent* to listen (from React state)
    const recognitionRef = useRef(null);
    const lastProcessedResultIndexRef = useRef(0);
    const isManualStopRef = useRef(false);
    const sessionStartTimeRef = useRef(null); // Track session start time for performance monitoring
    const processedSegmentsCountRef = useRef(0); // Track number of processed segments
    const isStartingRef = useRef(false); // Prevent race conditions during start/stop operations
    const restartTimeoutRef = useRef(null); // Track restart timeouts to prevent multiple restarts
    const lastResultTimeRef = useRef(Date.now()); // Watchdog to detect inactivity
    const restartCountRef = useRef(0); // Track restarts to trigger full recycle
    const lastInterimUpdateTimeRef = useRef(0); // Throttle interim updates

    const recreateRecognition = useCallback((SpeechRecognitionAPI) => {
        try {
            if (recognitionRef.current) {
                recognitionRef.current.onresult = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.onstart = null;
                recognitionRef.current.onend = null;
                try { recognitionRef.current.stop(); } catch {}
            }
            recognitionRef.current = new SpeechRecognitionAPI();
            // Keep recognition continuous to avoid frequent hard stops from the browser
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true; // show interim results
            recognitionRef.current.lang = dictationLang;
            // Allow a few alternatives to improve chance of a correct result
            if ('maxAlternatives' in recognitionRef.current) recognitionRef.current.maxAlternatives = 3;
            restartCountRef.current = 0;
        } catch (e) {
            console.error('Error recreating recognition instance:', e);
        }
    }, [dictationLang]);

    // Effect to keep listeningIntentRef in sync with listening state
    useEffect(() => {
        listeningIntentRef.current = listening;
    }, [listening]);

    // Performance monitoring and automatic session reset (disabled per user request to avoid auto resets)
    // To re-enable, set ENABLE_PERFORMANCE_RESET to true.
    const ENABLE_PERFORMANCE_RESET = false;
    useEffect(() => {
        if (!listening || !ENABLE_PERFORMANCE_RESET) return;
        const performanceCheckInterval = setInterval(() => {
            const sessionDuration = sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : 0;
            const segmentsProcessed = processedSegmentsCountRef.current;
            if (sessionDuration > 85000 || segmentsProcessed > 40) {
                console.log(`(Disabled) Would reset speech recognition session for performance. Duration: ${sessionDuration}ms, Segments: ${segmentsProcessed}`);
            }
        }, 15000);
        return () => clearInterval(performanceCheckInterval);
    }, [listening, ENABLE_PERFORMANCE_RESET]);

    // Inactivity watchdog (disabled). To re-enable auto recovery restarts, set ENABLE_INACTIVITY_WATCHDOG to true.
    const ENABLE_INACTIVITY_WATCHDOG = false;
    useEffect(() => {
        if (!listening || !ENABLE_INACTIVITY_WATCHDOG) return;
        const watchdog = setInterval(() => {
            const now = Date.now();
            const msSinceLastResult = now - (lastResultTimeRef.current || 0);
            if (listeningIntentRef.current && recognitionRef.current && !isStartingRef.current && msSinceLastResult > 18000) {
                console.log('(Disabled) Would attempt soft restart after inactivity');
            }
        }, 3000);
        return () => clearInterval(watchdog);
    }, [listening, ENABLE_INACTIVITY_WATCHDOG]);

    const processSegmentForInsertion = useCallback((segment) => {
        if (!segment) return; // segment from STT

        // Increment processed segments counter for performance monitoring
        processedSegmentsCountRef.current += 1;

        // Use the raw segment for command checking, as STT might include spaces
        const rawCommandText = segment.trim().toLowerCase(); // Trim for command matching only

        const baseLangCode = dictationLang.split('-')[0].toLowerCase();
        let commandsForLang = COMMANDS_FLAT[baseLangCode];
        if (!commandsForLang) {
            if (baseLangCode === 'en' && COMMANDS_FLAT['en']) {
                commandsForLang = COMMANDS_FLAT['en'];
            } else {
                commandsForLang = {};
            }
        }

        let textToInsert = segment; // Start with the original segment from STT
        let commandHandled = false;
        const commandMatch = commandsForLang[rawCommandText];

        if (commandMatch) {
            commandHandled = true;
            if (commandMatch.symbol !== undefined) {
                textToInsert = commandMatch.symbol; // Replace segment with symbol
            } else if (commandMatch.action) {
                textToInsert = ''; // No text insertion for action commands
                switch (commandMatch.action) {
                    case 'stopMic':
                        if (isListeningRef.current && recognitionRef.current) {
                            isManualStopRef.current = true;
                            recognitionRef.current.stop();
                        }
                        break;
                    case 'newline': insertRawTextIntoQuill(quillRef, '\n'); break;
                    case 'newparagraph': insertRawTextIntoQuill(quillRef, '\n\n'); break;
                    case 'clearEditor': handleClear(); break;
                    default:
                        commandHandled = false; // Unrecognized action
                        textToInsert = segment; // Revert to original segment
                        break;
                }
            } else { // Command found but no symbol or action
                commandHandled = false;
                textToInsert = segment; // Revert to original segment
            }
        }

        if (commandHandled && commandMatch && commandMatch.action && 
            (commandMatch.action === 'stopMic' || commandMatch.action === 'clearEditor' || commandMatch.action === 'newline' || commandMatch.action === 'newparagraph')) {
            // Do nothing further for these actions, they've been handled.
        } else if (textToInsert.length > 0) { // Proceed if there's text to insert (original segment or a symbol)
            
            const trimmedTextToInsert = textToInsert.trim();

            if (trimmedTextToInsert.length > 0) { // Ensure there's actual content after trimming
                let finalText = trimmedTextToInsert;

                const quill = quillRef.current?.getEditor();
                const selection = quill?.getSelection();
                let precedingChar = '';
                if (quill && selection && selection.index > 0) {
                    precedingChar = quill.getText(selection.index - 1, 1);
                }

                const startsWithSpaceConsumingPunctuation = /^[.,;:?!)।\]}>]/.test(finalText); // Punctuation that "consumes" a preceding space
                const endsWithSpaceAddingPunctuation = /[([{<]$/.test(precedingChar); // Punctuation that "adds" a space after itself

                if (precedingChar && precedingChar !== ' ' && precedingChar !== '\n' && !startsWithSpaceConsumingPunctuation && !endsWithSpaceAddingPunctuation) {
                    finalText = ' ' + finalText;
                }
                
                const endsWithPunctuationOrNewline = /[.,;:?!-/—।\n]$/.test(finalText.trim()); // Includes Devanagari Danda
                const endsWithSpace = /\s$/.test(finalText);

                if (!endsWithPunctuationOrNewline && !endsWithSpace) {
                    finalText += ' ';
                }
                
                insertRawTextIntoQuill(quillRef, finalText);
            }
        }
    }, [dictationLang, quillRef, handleClear]);

    useEffect(() => {
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognitionAPI) {
            setBrowserSupportsSpeechRecognition(false);
            showError("Speech recognition not supported in this browser. Use Chrome or Edge.");
            return;
        }

        setBrowserSupportsSpeechRecognition(true);
        recreateRecognition(SpeechRecognitionAPI);

        const attachHandlers = () => {
            if (!recognitionRef.current) return;

            recognitionRef.current.onresult = (event) => {
                if (!quillRef.current) return;
                lastResultTimeRef.current = Date.now();

                let finalizedTextForThisEvent = '';
                let fullInterimForThisEvent = '';

                for (let i = lastProcessedResultIndexRef.current; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalizedTextForThisEvent += transcript;
                    } else {
                        fullInterimForThisEvent += transcript;
                    }
                }

                // Update interim text state for display component (throttled)
                const now = Date.now();
                if (fullInterimForThisEvent.trim() && now - (lastInterimUpdateTimeRef.current || 0) >= 80) {
                    const maxInterimLength = 180;
                    const trimmedInterim = fullInterimForThisEvent.length > maxInterimLength
                        ? fullInterimForThisEvent.substring(0, maxInterimLength) + '...'
                        : fullInterimForThisEvent;
                    setInterimText(trimmedInterim);
                    lastInterimUpdateTimeRef.current = now;
                }

                // Only process final results in the editor
                if (finalizedTextForThisEvent.trim()) {
                    // Clear interim text since we have final results
                    setInterimText('');
                    processSegmentForInsertion(finalizedTextForThisEvent);
                    lastProcessedResultIndexRef.current = event.results.length;
                }
            };

            recognitionRef.current.onstart = () => {
                // reflect active listening in both refs and React state so UI stays in sync
                isListeningRef.current = true;
                isStartingRef.current = false;
                lastProcessedResultIndexRef.current = 0;
                sessionStartTimeRef.current = Date.now();
                processedSegmentsCountRef.current = 0;
                lastResultTimeRef.current = Date.now();
                // Clear any existing interim text when starting
                setInterimText('');
                try {
                    // set React state to true so parent UI shows listening when auto-restarts occur
                    setListening(true);
                } catch (e) {
                    // ignore if setListening isn't available
                }
                console.log('Speech recognition session started');
            };

            recognitionRef.current.onerror = (event) => {
                console.error("Speech recognition error:", event.error, event);
                isListeningRef.current = false;

                // Clear interim text on error
                setInterimText('');

                let shouldSetListeningFalse = true;
                let shouldAttemptRestart = false;
                if (event.error === 'not-allowed' || event.error === 'permission-denied') {
                    setIsMicrophoneAvailable(false);
                    showError("Microphone access denied. Please allow access in browser settings.");
                } else if (event.error === 'no-speech') {
                    shouldSetListeningFalse = false;
                    shouldAttemptRestart = true;
                } else if (event.error === 'audio-capture') {
                    showError("Audio capture failed. Check your microphone.");
                } else if (event.error === 'network') {
                    shouldAttemptRestart = true;
                    shouldSetListeningFalse = false;
                } else if (event.error === 'aborted') {
                    shouldSetListeningFalse = false;
                } else {
                    showError(`Speech recognition error: ${event.error}`);
                }

                if (shouldSetListeningFalse) setListening(false);

                if (shouldAttemptRestart && listeningIntentRef.current && !isStartingRef.current) {
                    if (restartTimeoutRef.current) {
                        clearTimeout(restartTimeoutRef.current);
                        restartTimeoutRef.current = null;
                    }
                    restartTimeoutRef.current = setTimeout(() => {
                        if (listeningIntentRef.current && recognitionRef.current && !isStartingRef.current) {
                            try {
                                lastProcessedResultIndexRef.current = 0;
                                processedSegmentsCountRef.current = 0;
                                isStartingRef.current = true;
                                recognitionRef.current.start();
                            } catch (e) {
                                console.error('Error restarting after recoverable error:', e);
                                isStartingRef.current = false;
                                setListening(false);
                            }
                        }
                        restartTimeoutRef.current = null;
                    }, 1000);
                } else {
                    lastProcessedResultIndexRef.current = 0;
                    processedSegmentsCountRef.current = 0;
                }
            };

            recognitionRef.current.onend = () => {
                const wasThisSessionManuallyStopped = isManualStopRef.current;
                const sessionDuration = sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : 0;
                console.log(`Speech recognition session ended. Duration: ${sessionDuration}ms, Segments processed: ${processedSegmentsCountRef.current}`);

                isListeningRef.current = false;
                // Clear interim text when session ends
                setInterimText('');

                if (wasThisSessionManuallyStopped) {
                    isManualStopRef.current = false;
                    if (listeningIntentRef.current) setListening(false);
                    lastProcessedResultIndexRef.current = 0;
                    processedSegmentsCountRef.current = 0;
                } else if (listeningIntentRef.current && recognitionRef.current && !isStartingRef.current) {
                    if (restartTimeoutRef.current) {
                        clearTimeout(restartTimeoutRef.current);
                        restartTimeoutRef.current = null;
                    }
                    const restartDelay = Math.min(400 + (processedSegmentsCountRef.current * 8), 1500);
                    restartTimeoutRef.current = setTimeout(() => {
                        if (listeningIntentRef.current && recognitionRef.current && !isStartingRef.current) {
                            try {
                                isManualStopRef.current = false;
                                lastProcessedResultIndexRef.current = 0;
                                processedSegmentsCountRef.current = 0;
                                isStartingRef.current = true;
                                restartCountRef.current += 1;
                                const needRecycle = restartCountRef.current >= 6 || (sessionStartTimeRef.current && (Date.now() - sessionStartTimeRef.current) > 180000);
                                if (needRecycle) {
                                    console.log('Recycling speech recognition instance for freshness');
                                    recreateRecognition(SpeechRecognitionAPI);
                                    attachHandlers();
                                    sessionStartTimeRef.current = Date.now();
                                    restartCountRef.current = 0;
                                }
                                recognitionRef.current.start();
                            } catch (e) {
                                console.error('Error restarting recognition:', e);
                                isStartingRef.current = false;
                                setListening(false);
                                isListeningRef.current = false;
                            }
                        } else {
                            if (listeningIntentRef.current) setListening(false);
                            isListeningRef.current = false;
                        }
                        restartTimeoutRef.current = null;
                    }, restartDelay);
                } else {
                    if (listeningIntentRef.current) setListening(false);
                    isListeningRef.current = false;
                    lastProcessedResultIndexRef.current = 0;
                    processedSegmentsCountRef.current = 0;
                }
            };
        };

        attachHandlers();

        return () => {
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current);
                restartTimeoutRef.current = null;
            }
            if (recognitionRef.current) {
                isManualStopRef.current = true;
                isStartingRef.current = false;
                try {
                    recognitionRef.current.onresult = null;
                    recognitionRef.current.onerror = null;
                    recognitionRef.current.onstart = null;
                    recognitionRef.current.onend = null;
                    recognitionRef.current.stop();
                } catch (e) {
                    console.error('Error stopping recognition on cleanup:', e);
                }
                recognitionRef.current = null;
                isListeningRef.current = false;
                setListening(false);
            }
        };
    }, [dictationLang, showError, processSegmentForInsertion, quillRef, recreateRecognition]);

    const startRecognition = useCallback(() => {
        if (isStartingRef.current || restartTimeoutRef.current) {
            console.log('Recognition start prevented - already starting or restart pending');
            return;
        }
        isManualStopRef.current = false;
        lastProcessedResultIndexRef.current = 0;
        sessionStartTimeRef.current = Date.now();
        processedSegmentsCountRef.current = 0;
        isStartingRef.current = true;

        if (recognitionRef.current) {
            recognitionRef.current.lang = dictationLang;
            if ('maxAlternatives' in recognitionRef.current) {
                recognitionRef.current.maxAlternatives = 3;
            }
            try {
                recognitionRef.current.start();
                console.log(`Starting speech recognition for language: ${dictationLang}`);
            } catch (e) {
                console.error('Error starting recognition:', e);
                isStartingRef.current = false;
                throw e;
            }
        }
    }, [dictationLang]);

    const stopRecognition = useCallback(() => {
        isManualStopRef.current = true;
        isStartingRef.current = false;

        // Clear interim text when manually stopping
        setInterimText('');

        // Clear any pending restart timeouts
        if (restartTimeoutRef.current) {
            clearTimeout(restartTimeoutRef.current);
            restartTimeoutRef.current = null;
        }

        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {
                console.error("Error calling recognition.stop():", e);
            }
        }
    }, []);

    return {
        listening,
        setListening,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        recognitionRef,
        isListeningRef,
        startRecognition,
        stopRecognition,
        interimText, // Add interim text for display component
    };
};

export default useSpeechRecognition;